"use client"

import { useState } from "react"
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible"
import { ChevronDown, ChevronUp } from "lucide-react"
import { ClientProgressBar } from "@/components/ClientProgressBar"
import { BlurredContent } from "@/components/BlurredContent"

interface SurveyData {
  studentSurvey5: { [key: string]: { [key: string]: number } }
  studentSurvey8: { [key: string]: { [key: string]: number } }
  teacherSurvey: { [key: string]: { [key: string]: number } }
  parentSurvey: { [key: string]: { [key: string]: number } }
}

interface SchoolData {
  skolenhetskod: string
  name: string
  skolkod: string
}

export default function CollapsibleSurveyResults({
  surveyData,
  schoolData,
}: { surveyData: SurveyData; schoolData: SchoolData }) {
  // Extract the school ID and name for the BlurredContent component
  const schoolId = schoolData.skolenhetskod.toString()
  const schoolName = schoolData.name

  const [isOpen5, setIsOpen5] = useState(false)
  const [isOpen8, setIsOpen8] = useState(false)
  const [isOpenTeachers, setIsOpenTeachers] = useState(false)
  const [isOpenParents, setIsOpenParents] = useState(false)

  const surveyAverages = {
    "Årskurs 5": {
      "Information om utbildningen": 6.7,
      Stimulans: 6.5,
      Stöd: 7.4,
      "Kritiskt tänkande": 6.5,
      "Bemötande - lärare": 7.4,
      "Bemötande - elever": 6.9,
      Inflytande: 5.8,
      Studiero: 5.6,
      Trygghet: 8.1,
      "Förhindra kränkningar": 6.3,
      Elevhälsa: 6.8,
      "Övergripande nöjdhet": 6.9,
    },
    "Årskurs 8": {
      "Information om utbildningen": 5.3,
      Stimulans: 5.4,
      Stöd: 6.4,
      "Kritiskt tänkande": 5.7,
      "Bemötande - lärare": 6.7,
      "Bemötande - elever": 6.5,
      Inflytande: 4.6,
      Studiero: 5.6,
      Trygghet: 7.8,
      "Förhindra kränkningar": 5.4,
      Elevhälsa: 5.8,
      "Övergripande nöjdhet": 5.8,
    },
    Lärare: {
      "Information om utbildningen": 7.4,
      Samverkan: 6.6,
      Stimulans: 7.0,
      Stöd: 6.4,
      "Kritiskt tänkande": 6.7,
      "Bemötande - elever": 6.7,
      Jämställdhet: 8.1,
      Elevinflytande: 6.2,
      Studiero: 6.9,
      Trygghet: 6.7,
      "Förhindra kränkningar": 7.3,
      "Särskilt stöd": 6.0,
      "Bedömning och betygsättning": 6.2,
      "Pedagogiskt ledarskap": 7.1,
      Uppföljning: 7.6,
      Elevhälsa: 6.8,
    },
    Vårdnadshavare: {
      "Information från skolan": 6.4,
      Stimulans: 6.9,
      Stöd: 7.0,
      "Bemötande - lärare": 8.1,
      "Bemötande - elever": 7.1,
      Studiero: 6.0,
      Trygghet: 7.7,
      Elevhälsa: 6.9,
      "Övergripande nöjdhet": 6.9,
    },
  }

  const renderSurveyResults = (data: { [key: string]: number }, averages: { [key: string]: number }, title: string) => {
    const visibleItems = ["Information om utbildningen", "Stimulans", "Stöd", "Studiero", "Trygghet"]

    return (
      <div className="p-4 space-y-4 bg-gray-50 rounded-b-lg border border-t-0 border-gray-300">
        {Object.entries(data || {}).map(([key, value]) => (
          <div key={key} className="mb-4">
            <div className="flex justify-between mb-1">
              <span>{key}</span>
            </div>
            {visibleItems.includes(key) ? (
              <ClientProgressBar percentage={(value / 10) * 100} score={value} average={averages[key] || 0} />
            ) : (
              <BlurredContent schoolId={schoolId} schoolName={schoolName}>
                <ClientProgressBar percentage={(value / 10) * 100} score={value} average={averages[key] || 0} />
              </BlurredContent>
            )}
          </div>
        ))}
        <p className="text-xs text-gray-500 mt-4">
          {title} Skala 0-10. Blå stapel är skolans värde. Gul linje är genomsnittet.
        </p>
      </div>
    )
  }

  return (
    <>
      <Collapsible open={isOpen5} onOpenChange={setIsOpen5}>
        <CollapsibleTrigger asChild>
          <button className="w-full text-left bg-blue-100 hover:bg-blue-200 transition-colors p-4 flex justify-between items-center rounded-lg mt-4">
            <span className="text-lg font-semibold text-gray-800">Elevenkät årskurs 5</span>
            {isOpen5 ? (
              <ChevronUp className="text-blue-600 flex-shrink-0" size={24} />
            ) : (
              <ChevronDown className="text-blue-600 flex-shrink-0" size={24} />
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {surveyData.studentSurvey5[schoolData.skolkod] ? (
            renderSurveyResults(
              surveyData.studentSurvey5[schoolData.skolkod],
              surveyAverages["Årskurs 5"],
              "Resultat från elevenkät årskurs 5.",
            )
          ) : (
            <p>Ingen data tillgänglig för elevenkät årskurs 5.</p>
          )}
        </CollapsibleContent>
      </Collapsible>

      <Collapsible open={isOpen8} onOpenChange={setIsOpen8}>
        <CollapsibleTrigger asChild>
          <button className="w-full text-left bg-blue-100 hover:bg-blue-200 transition-colors p-4 flex justify-between items-center rounded-lg mt-4">
            <span className="text-lg font-semibold text-gray-800">Elevenkät årskurs 8</span>
            {isOpen8 ? (
              <ChevronUp className="text-blue-600 flex-shrink-0" size={24} />
            ) : (
              <ChevronDown className="text-blue-600 flex-shrink-0" size={24} />
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {surveyData.studentSurvey8[schoolData.skolkod] ? (
            renderSurveyResults(
              surveyData.studentSurvey8[schoolData.skolkod],
              surveyAverages["Årskurs 8"],
              "Resultat från elevenkät årskurs 8.",
            )
          ) : (
            <p>Ingen data tillgänglig för elevenkät årskurs 8.</p>
          )}
        </CollapsibleContent>
      </Collapsible>

      <Collapsible open={isOpenTeachers} onOpenChange={setIsOpenTeachers}>
        <CollapsibleTrigger asChild>
          <button className="w-full text-left bg-blue-100 hover:bg-blue-200 transition-colors p-4 flex justify-between items-center rounded-lg mt-4">
            <span className="text-lg font-semibold text-gray-800">Skolenkät - lärare</span>
            {isOpenTeachers ? (
              <ChevronUp className="text-blue-600 flex-shrink-0" size={24} />
            ) : (
              <ChevronDown className="text-blue-600 flex-shrink-0" size={24} />
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {surveyData.teacherSurvey[schoolData.skolkod] ? (
            renderSurveyResults(
              surveyData.teacherSurvey[schoolData.skolkod],
              surveyAverages["Lärare"],
              "Resultat från lärarenkät.",
            )
          ) : (
            <p>Ingen data tillgänglig för lärarenkäten.</p>
          )}
        </CollapsibleContent>
      </Collapsible>

      <Collapsible open={isOpenParents} onOpenChange={setIsOpenParents}>
        <CollapsibleTrigger asChild>
          <button className="w-full text-left bg-blue-100 hover:bg-blue-200 transition-colors p-4 flex justify-between items-center rounded-lg mt-4">
            <span className="text-lg font-semibold text-gray-800">Skolenkät - vårdnadshavare</span>
            {isOpenParents ? (
              <ChevronUp className="text-blue-600 flex-shrink-0" size={24} />
            ) : (
              <ChevronDown className="text-blue-600 flex-shrink-0" size={24} />
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {surveyData.parentSurvey[schoolData.skolkod] ? (
            renderSurveyResults(
              surveyData.parentSurvey[schoolData.skolkod],
              surveyAverages["Vårdnadshavare"],
              "Resultat från vårdnadshavarenkät.",
            )
          ) : (
            <p>Ingen data tillgänglig för vårdnadshavarenkäten.</p>
          )}
        </CollapsibleContent>
      </Collapsible>
    </>
  )
}

